<template>
  <div class="editor-wrapper" :class="{ 'mobile-editor': isMobile }">
    <div :id="editorId" class="wang-editor-container"></div>
  </div>
</template>

<script>
import Editor from 'wangeditor'
import 'wangeditor/release/wangEditor.min.css'
import { oneOf } from '@/libs/tools'
import { baseUrl } from '../../../vue.config'
let nid = -1

export default {
  name: 'Editor',
  props: {
    value: {
      type: String,
      default: ''
    },
    /**
     * 绑定的值的类型, enum: ['html', 'text']
     */
    valueType: {
      type: String,
      default: 'html',
      validator: (val) => {
        return oneOf(val, ['html', 'text'])
      }
    },
    /**
     * @description 设置change事件触发时间间隔
     */
    changeInterval: {
      type: Number,
      default: 200
    },
    /**
     * @description 是否开启本地存储
     */
    cache: {
      type: Boolean,
      default: true
    },
    /**
     * @description 是否为移动端模式
     */
    mobile: {
      type: Boolean,
      default: false
    }
  },

  data () {
    return {
      editor: null,
      isMobile: this.mobile || this.checkMobile()
    }
  },

  computed: {
    editorId () {
      return `editor${this._uid}`
    }
  },

  mounted () {
    this.editor = new Editor(`#${this.editorId}`)

    // 应用配置
    Object.assign(this.editor.customConfig, this.initEditorConfig())

    // 内容变化回调
    this.editor.customConfig.onchange = (html) => {
      let text = this.editor.txt.text()
      if (this.cache) localStorage.editorCache = html
      this.$emit('input', this.valueType === 'html' ? html : text)
      this.$emit('on-change', html, text)
    }

    // 设置变化触发时间间隔
    this.editor.customConfig.onchangeTimeout = this.changeInterval

    // 配置图片上传
    this.configImageUpload()

    // 响应式处理
    this.handleResize()
    window.addEventListener('resize', this.handleResize)
  },

  methods: {

     // 检测是否为移动端
    checkMobile () {
      return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent) || window.innerWidth <= 768
    },

    // 移动端工具栏配置
    getMobileMenus () {
      return [
        'head',
        'bold',
        'italic',
        'underline',
        'strikeThrough',
        'foreColor',
        'backColor',
        'link',
        'list',
        'justify',
        'quote',
        'image',
        'undo',
        'redo'
      ]
    },

    // 桌面端工具栏配置
    getDesktopMenus () {
      return [
        'head',
        'bold',
        'fontSize',
        'fontName',
        'italic',
        'underline',
        'strikeThrough',
        'foreColor',
        'backColor',
        'link',
        'list',
        'justify',
        'quote',
        'emoticon',
        'image',
        'table',
        'video',
        'code',
        'splitLine',
        'undo',
        'redo'
      ]
    },

    // 初始化编辑器配置
    initEditorConfig () {
      const config = {
        // 配置菜单栏
        menus: this.isMobile ? this.getMobileMenus() : this.getDesktopMenus(),

        // 颜色配置
        colors: [
          '#000000', '#eeece0', '#1c487f', '#4d80bf',
          '#c24f4a', '#8baa4a', '#7b5ba1', '#46acc8',
          '#f9963b', '#ffffff'
        ],

        // 字体配置
        fontNames: [
          '黑体',
          '仿宋',
          '楷体',
          '标楷体',
          '华文仿宋',
          '华文楷体',
          '宋体',
          '微软雅黑',
          'Arial',
          'Tahoma',
          'Verdana',
          'Times New Roman',
          'Courier New'
        ],

        // 字号配置
        fontSizes: {
          'x-small': { name: '10px', value: '1' },
          'small': { name: '13px', value: '2' },
          'normal': { name: '16px', value: '3' },
          'large': { name: '18px', value: '4' },
          'x-large': { name: '24px', value: '5' },
          'xx-large': { name: '32px', value: '6' },
          'xxx-large': { name: '48px', value: '7' }
        },

        // 层级配置
        zIndex: 100,

        // 占位符
        placeholder: this.isMobile ? '开始编写内容...' : '请输入正文内容',

        // 移动端配置
        ...(this.isMobile ? {
          // 移动端特有配置
          height: 300,
          showFullScreen: false
        } : {
          // 桌面端配置
          height: 400,
          showFullScreen: true
        })
      }

      return config
    },

    // 配置图片上传
    configImageUpload () {
      // 将图片大小限制
      this.editor.customConfig.uploadImgMaxSize = this.isMobile ? 2 * 1024 * 1024 : 5 * 1024 * 1024 // 移动端2M，桌面端5M

      // 限制一次最多上传图片数量
      this.editor.customConfig.uploadImgMaxLength = this.isMobile ? 3 : 9

      // 上传图片的错误提示
      this.editor.customConfig.customAlert = function (info) {
        console.warn('编辑器提示：', info)
      }

      // 上传图片到服务器
      this.editor.customConfig.uploadImgServer = '/v1/newsinfo/upload/'
      this.editor.customConfig.uploadFileName = 'imgdata'

      // 移动端图片处理配置
      if (this.isMobile) {
        // 移动端自动压缩图片
        this.editor.customConfig.uploadImgShowBase64 = true
      }

      // 上传图片的钩子函数
      this.editor.customConfig.uploadImgHooks = {
        before: (xhr, editor, files) => {
          console.log('开始上传图片:', files)

          // 移动端图片预处理
          if (this.isMobile && files.length > 0) {
            this.compressImages(files)
          }
        },
        success: (xhr, editor, result) => {
          console.log('图片上传成功:', result)
        },
        fail: (xhr, editor, result) => {
          console.log('图片上传失败:', result)
        },
        error: (xhr, editor) => {
          console.error('图片上传出错:', xhr)
        },
        customInsert: (insertImg, result, editor) => {
          if (result.code == 0) {
            var url = result.filename
            const fullUrl = baseUrl + 'upload-image/news/' + url
            insertImg(fullUrl)
          }
        }
      }
    },

    // 移动端图片压缩
    compressImages (files) {
      // 这里可以添加图片压缩逻辑
      // 由于限制，这里只是示例，实际使用时可以集成图片压缩库
      console.log('移动端图片压缩处理:', files)
    },

    setHtml (val, nid) {
      if (!this.editor) return

      this.editor.customConfig.uploadImgParams = {
        nid: nid
      }

      if (!this.editor.isCreated) {
        this.editor.create()
      }

      this.editor.txt.html(val || '')
    },

    // 获取内容
    getHtml () {
      return this.editor ? this.editor.txt.html() : ''
    },

    // 获取文本
    getText () {
      return this.editor ? this.editor.txt.text() : ''
    },

    // 清空内容
    clear () {
      if (this.editor) {
        this.editor.txt.clear()
      }
    },

    // 禁用编辑器
    disable () {
      if (this.editor) {
        this.editor.disable()
      }
    },

    // 启用编辑器
    enable () {
      if (this.editor) {
        this.editor.enable()
      }
    },
    // 处理窗口大小变化
    handleResize () {
      const newIsMobile = this.checkMobile()
      if (newIsMobile !== this.isMobile) {
        this.isMobile = newIsMobile
        // 重新初始化编辑器以应用新配置
        if (this.editor) {
          const currentContent = this.editor.txt.html()
          this.editor.destroy()
          this.$nextTick(() => {
            this.editor = new Editor(`#${this.editorId}`)
            Object.assign(this.editor.customConfig, this.initEditorConfig())
            this.configImageUpload()
            this.editor.create()
            this.editor.txt.html(currentContent)
          })
        }
      }
    }
  },

  beforeDestroy () {
    if (this.editor) {
      this.editor.destroy()
      this.editor = null
    }
    window.removeEventListener('resize', this.handleResize)
  }
}
</script>

<style lang="less" scoped>
.editor-wrapper {
  .wang-editor-container {
    border: 1px solid #ccc;
    border-radius: 4px;
    overflow: hidden;
  }

  // 移动端样式
  &.mobile-editor {
    .wang-editor-container {
      /deep/ .w-e-toolbar {
        background: #f8f8f8;
        border-bottom: 1px solid #e8e8e8;
        padding: 8px 4px;

        .w-e-menu {
          width: auto;
          height: 36px;
          margin: 2px;

          .w-e-drop-list {
            max-height: 200px;
            overflow-y: auto;
          }
        }

        // 移动端工具栏按钮
        .w-e-menu .w-e-menu-item {
          width: 36px;
          height: 36px;
          padding: 6px;
          margin: 1px;
          border-radius: 4px;

          &:hover {
            background-color: #e6f7ff;
          }

          &.w-e-active {
            background-color: #1890ff;
            color: #fff;
          }
        }
      }

      .w-e-text-container {
        .w-e-text {
          padding: 12px 16px;
          font-size: 16px;
          line-height: 1.6;

          // 移动端内容样式
          p {
            margin-bottom: 12px;
            word-wrap: break-word;
            word-break: break-all;
          }

          img {
            max-width: 100%;
            height: auto;
            border-radius: 4px;
            margin: 8px 0;
            box-shadow: 0 2px 8px rgba(0,0,0,0.1);
          }

          blockquote {
            border-left: 4px solid #1890ff;
            padding-left: 12px;
            margin: 16px 0;
            color: #666;
            background: #f9f9f9;
            padding: 12px;
            border-radius: 4px;
          }

          pre {
            background: #f5f5f5;
            padding: 12px;
            border-radius: 4px;
            overflow-x: auto;
          }

          table {
            width: 100%;
            border-collapse: collapse;
            margin: 16px 0;

            th, td {
              border: 1px solid #e8e8e8;
              padding: 8px 12px;
              text-align: left;
            }

            th {
              background: #fafafa;
              font-weight: 500;
            }
          }
        }
      }
    }
  }

  // 桌面端样式增强
  &:not(.mobile-editor) {
    .wang-editor-container {
      /deep/ .w-e-toolbar {
        .w-e-menu {
          transition: all 0.2s;

          &:hover {
            background-color: #f0f0f0;
          }
        }
      }

      .w-e-text-container {
        .w-e-text {
          padding: 16px 20px;

          img {
            cursor: pointer;
            transition: transform 0.2s;

            &:hover {
              transform: scale(1.02);
            }
          }
        }
      }
    }
  }
}

// 全局编辑器样式调整
/deep/ .w-e-text-container .w-e-text {
  color: #333;

  h1, h2, h3, h4, h5, h6 {
    margin: 20px 0 16px 0;
    font-weight: 600;
    color: #262626;
  }

  h1 { font-size: 24px; }
  h2 { font-size: 20px; }
  h3 { font-size: 18px; }
  h4 { font-size: 16px; }
  h5 { font-size: 14px; }
  h6 { font-size: 12px; }

  ul, ol {
    padding-left: 24px;
    margin: 12px 0;

    li {
      margin-bottom: 6px;
      line-height: 1.6;
    }
  }

  a {
    color: #1890ff;
    text-decoration: none;

    &:hover {
      text-decoration: underline;
    }
  }
}

// 媒体查询
@media screen and (max-width: 768px) {
  .editor-wrapper .wang-editor-container {
    /deep/ .w-e-toolbar {
      padding: 6px 2px;
      flex-wrap: wrap;

      .w-e-menu {
        width: 32px;
        height: 32px;
        margin: 1px;

        .w-e-menu-item {
          width: 32px;
          height: 32px;
          padding: 4px;
        }
      }
    }
  }
}

@media screen and (max-width: 480px) {
  .editor-wrapper .wang-editor-container {
    /deep/ .w-e-text-container .w-e-text {
      padding: 12px;
      font-size: 14px;

      h1 { font-size: 20px; }
      h2 { font-size: 18px; }
      h3 { font-size: 16px; }
      h4, h5, h6 { font-size: 14px; }
    }
  }
}
</style>
